import React from 'react';
import {
  AvailabilityMap,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import {
  getVisibleQuantity,
  QuantityStateType,
} from '@/screens/new-order/hooks/use-quantity-reducer';
import {
  ItemDetailActions,
  RenderActionsFn,
} from '@/components/orders/item-detail-actions';
import { OrderableItemListItemHeading } from '@/components/orders/orderable-item-list-item-heading';
import { StyleSheet, View } from 'react-native';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useGetAddToOrderDisabled } from '@/hooks/use-add-to-order-disabled';
import { VariantPackOrderInfo } from '@/components/orders/variant-pack-order-info';

export interface Props {
  availabilityMap?: AvailabilityMap;
  canDecrementToZero?: boolean;
  disabled?: boolean;
  disableTextInput?: boolean;
  headingNumberOfLines?: number;
  inDraggableSheet?: boolean;
  isFocused: boolean;
  quantityState?: QuantityStateType;
  renderActions?: RenderActionsFn;
  renderOrderGuideButtonAction?: (variantPackUUID: string) => React.ReactNode;
  showTooltipOnMount?: boolean;
  showVariantPackOrderInfo?: boolean;
  skus: ItemListOptionMetadata[];
  updateQuantity?: UpdateQuantityFn;
}

export const CompactOrderableItem: React.FC<Props> = ({
  availabilityMap,
  canDecrementToZero,
  disabled,
  disableTextInput,
  headingNumberOfLines,
  inDraggableSheet,
  isFocused,
  skus,
  quantityState,
  renderActions,
  renderOrderGuideButtonAction,
  showTooltipOnMount,
  showVariantPackOrderInfo,
  updateQuantity,
}) => {
  const getAddToOrderDisabled = useGetAddToOrderDisabled();

  return (
    <View style={styles.base}>
      {skus.map((metadata, index) => {
        const isAddToOrderDisabled = getAddToOrderDisabled(
          metadata.variantPackUUID,
        );
        return (
          <View
            key={`orderable-item-list-item-wrapper-${metadata.variantPackUUID}`}
            style={styles.skuContainer}
          >
            <View
              key={`orderable-item-list-item-${
                metadata.variantPackUUID
              }-${metadata.paidSearch ? 'paid-search' : 'base'}`}
              style={styles.compactSkuWrapper}
            >
              <ItemDetailActions
                canDecrementToZero={canDecrementToZero}
                disabled={disabled}
                disableTextInput={disableTextInput}
                heading={
                  index === 0 ? (
                    <OrderableItemListItemHeading
                      heading={metadata.displayName}
                      numberOfLines={headingNumberOfLines}
                    />
                  ) : undefined
                }
                inDraggableSheet={inDraggableSheet}
                isCompactView
                isFocused={isFocused}
                item={metadata}
                quantity={
                  quantityState &&
                  getVisibleQuantity(quantityState, metadata.variantPackUUID)
                }
                quantityInStock={availabilityMap?.[metadata.variantPackUUID]}
                renderOrderGuideButton={renderOrderGuideButtonAction}
                showInlineModifiers
                showListView={false}
                showTooltipOnMount={showTooltipOnMount}
                updateQuantity={updateQuantity}
                wrapperStyle={styles.contentWrapper}
              />
              {showVariantPackOrderInfo ? (
                <VariantPackOrderInfo variantPackMetadata={metadata} />
              ) : null}
            </View>
            {isAddToOrderDisabled ? null : renderActions?.(metadata)}
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  base: {
    gap: 32,
  },
  compactSkuWrapper: {
    flex: 1,
    gap: 8,
  },
  contentWrapper: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  skuContainer: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'space-between',
  },
});
